import { ILoginResponse } from '../../shared/models/core/auth';
import {
  getRefreshResult,
  getLogoutNotice,
  REFRESH_LOCK_KEY,
  SESSION_TAB_ID,
  setRefreshResult,
  subscribeToSessionEvents,
} from '../../utility/auth/sessionSync';
import { apiUrl } from '../../environments/environment';

const REFRESH_WEB_LOCK_NAME = 'epos-auth-refresh';
const REFRESH_LOCK_TTL_MS = 15 * 1000;
const REFRESH_WAIT_TIMEOUT_MS = 20 * 1000;
const REFRESH_RESULT_REUSE_MS = 5 * 1000;
const REFRESH_FAILURE_REUSE_MS = 15 * 1000;

interface IRefreshLock {
  ownerTabId: string;
  expiresAt: number;
}

let refreshPromise: Promise<ILoginResponse | null> | null = null;

const readRefreshLock = (): IRefreshLock | null => {
  const raw = window.localStorage.getItem(REFRESH_LOCK_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as IRefreshLock;
  } catch {
    return null;
  }
};

const releaseRefreshLock = () => {
  const currentLock = readRefreshLock();
  if (currentLock?.ownerTabId === SESSION_TAB_ID) {
    window.localStorage.removeItem(REFRESH_LOCK_KEY);
  }
};

const tryAcquireRefreshLock = () => {
  const now = Date.now();
  const currentLock = readRefreshLock();

  if (currentLock && currentLock.expiresAt > now && currentLock.ownerTabId !== SESSION_TAB_ID) {
    return false;
  }

  const nextLock: IRefreshLock = {
    ownerTabId: SESSION_TAB_ID,
    expiresAt: now + REFRESH_LOCK_TTL_MS,
  };

  window.localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(nextLock));

  return readRefreshLock()?.ownerTabId === SESSION_TAB_ID;
};

const hasRecentRefreshFailure = (): boolean => {
  const currentResult = getRefreshResult();
  return Boolean(
    currentResult &&
      !currentResult.ok &&
      Date.now() - currentResult.timestamp <= REFRESH_FAILURE_REUSE_MS
  );
};

const getReusableRefreshResult = (): ILoginResponse | null => {
  const currentResult = getRefreshResult();
  if (!currentResult?.ok || !currentResult.data) {
    return null;
  }

  if (Date.now() - currentResult.timestamp > REFRESH_RESULT_REUSE_MS) {
    return null;
  }

  return currentResult.data as ILoginResponse;
};

const waitForRefreshResult = async (): Promise<ILoginResponse | null> => {
  if (hasRecentRefreshFailure()) {
    return null;
  }

  const initialTimestamp = getRefreshResult()?.timestamp ?? 0;
  const reusableResult = getReusableRefreshResult();

  if (reusableResult) {
    return reusableResult;
  }

  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      unsubscribe();
      resolve(getReusableRefreshResult());
    }, REFRESH_WAIT_TIMEOUT_MS);

    const unsubscribe = subscribeToSessionEvents((event) => {
      if (event.type !== 'refreshCompleted') {
        return;
      }

      const currentResult = getRefreshResult();
      if (!currentResult || currentResult.timestamp <= initialTimestamp) {
        return;
      }

      window.clearTimeout(timeoutId);
      unsubscribe();
      resolve((currentResult.data as ILoginResponse | null) ?? null);
    });
  });
};

const performRefreshRequest = async (): Promise<ILoginResponse | null> => {
  if (getLogoutNotice() || hasRecentRefreshFailure()) {
    return null;
  }

  try {
    const response = await fetch(apiUrl('/api/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      setRefreshResult({
        timestamp: Date.now(),
        ok: false,
        data: null,
      });
      return null;
    }

    const data = (await response.json()) as ILoginResponse;
    setRefreshResult({
      timestamp: Date.now(),
      ok: true,
      data,
    });
    return data;
  } catch {
    setRefreshResult({
      timestamp: Date.now(),
      ok: false,
      data: null,
    });
    return null;
  } finally {
    releaseRefreshLock();
  }
};

const renewWithStorageLock = async (): Promise<ILoginResponse | null> => {
  if (getLogoutNotice() || hasRecentRefreshFailure()) {
    return null;
  }

  const reusableResult = getReusableRefreshResult();
  if (reusableResult) {
    return reusableResult;
  }

  if (tryAcquireRefreshLock()) {
    return performRefreshRequest();
  }

  return waitForRefreshResult();
};

const renewWithWebLock = async (): Promise<ILoginResponse | null> => {
  if (getLogoutNotice() || hasRecentRefreshFailure()) {
    return null;
  }

  const locks = 'locks' in navigator ? navigator.locks : undefined;

  if (!locks) {
    return renewWithStorageLock();
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REFRESH_WAIT_TIMEOUT_MS);

  try {
    return await locks.request(
      REFRESH_WEB_LOCK_NAME,
      { mode: 'exclusive', signal: controller.signal },
      async () => {
        const reusableResult = getReusableRefreshResult();
        if (reusableResult) {
          return reusableResult;
        }

        return performRefreshRequest();
      }
    );
  } catch {
    return renewWithStorageLock();
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const renewSession = async (): Promise<ILoginResponse | null> => {
  if (getLogoutNotice() || hasRecentRefreshFailure()) {
    return null;
  }

  refreshPromise ??= renewWithWebLock().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};
